'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId } from "react";
import { ClassNameConfigurator, prepareForSlot } from "@mui/base";
import { Input as BaseInput } from "@mui/base/Input";
import { cva } from "class-variance-authority";
import { cn } from "../../common/helpers.js";
import { errorClasses, helperClasses, labelClasses, requiredClasses } from "../../common/classes.js";
import TextMaskCustom from "./TextMaskCustom.js";
// pass label straight through without classes if it's a reactnode
export const rootClasses = cva([
    'flex',
    'outline',
    'outline-1',
    'rounded-lg',
    'w-full',
    'transition-[outline-color]',
    'duration-200',
    'focus-within:outline-2'
], {
    variants: {
        multiline: {
            true: [
                'h-[5.5rem]',
                'resize-y',
                'overflow-auto',
                'items-start'
            ],
            false: [
                'items-center'
            ]
        },
        disabled: {
            true: [
                'text-black/40',
                'bg-gray-100',
                'opacity-100',
                'cursor-not-allowed'
            ],
            false: [
                'bg-white'
            ]
        },
        error: {
            true: [
                'outline-danger'
            ],
            false: [
                'outline-gray-200'
            ]
        }
    },
    compoundVariants: [
        {
            disabled: false,
            error: false,
            className: [
                'hover:outline-gray-700',
                'focus-within:outline-primary',
                'hover:focus-within:outline-primary'
            ]
        },
        {
            disabled: false,
            error: true,
            className: [
                'hover:outline-danger',
                'focus-within:outline-danger',
                'hover:focus-within:outline-danger'
            ]
        }
    ]
});
export const inputClasses = cva([
    'appearance-none',
    'text-normal',
    'font-medium',
    'enabled:text-gray-700',
    'placeholder:text-black/30',
    'placeholder-shown:truncate',
    'bg-transparent',
    'focus:outline-none',
    'flex-grow',
    'resize-none',
    'leading-7'
], {
    variants: {
        disabled: {
            true: [
                'cursor-not-allowed'
            ],
            false: []
        },
        size: {
            sm: [
                'py-2',
                'px-5'
            ],
            md: [
                'py-4',
                'px-5'
            ]
        },
        startDecorator: {
            true: [],
            false: []
        },
        endDecorator: {
            true: [],
            false: []
        }
    },
    compoundVariants: [
        {
            size: 'sm',
            startDecorator: true,
            className: [
                'pl-2'
            ]
        },
        {
            size: 'sm',
            endDecorator: true,
            className: [
                'pr-2'
            ]
        },
        {
            size: 'md',
            startDecorator: true,
            className: [
                'pl-2'
            ]
        },
        {
            size: 'md',
            endDecorator: true,
            className: [
                'pr-2'
            ]
        }
    ]
});
const decoratorClasses = cva([], {
    variants: {
        multiline: {
            true: [
                'pt-5'
            ],
            false: []
        },
        position: {
            left: [
                'pl-5'
            ],
            right: [
                'pr-5'
            ]
        }
    }
});
/**
 * Text Field - [API](https://scl.sharp.com/?path=/docs/base-textfield) | [DSM](https://sharphealthcare.notion.site/Inputs-4a77e695a529479fa701e2f3dcdc47e7#effba4e1d9304ba19046b04cdfe8d97b)
 */ const TextField = /*#__PURE__*/ forwardRef((_param, ref)=>{
    var { helperText, mask, label, id, size = 'md', error, startDecorator, endDecorator, className } = _param, props = _object_without_properties(_param, [
        "helperText",
        "mask",
        "label",
        "id",
        "size",
        "error",
        "startDecorator",
        "endDecorator",
        "className"
    ]);
    const generatedID = useId();
    const inputID = id !== null && id !== void 0 ? id : generatedID;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('space-y-2', className),
        children: [
            label && /*#__PURE__*/ _jsx("label", {
                htmlFor: inputID,
                className: cn(labelClasses({
                    disabled: Boolean(props.disabled),
                    error: Boolean(error)
                })),
                children: label
            }),
            props.required && /*#__PURE__*/ _jsx("div", {
                "aria-label": "required",
                className: cn(requiredClasses({
                    disabled: Boolean(props.disabled)
                })),
                children: "*"
            }),
            helperText && /*#__PURE__*/ _jsx("div", {
                className: cn(helperClasses({
                    disabled: Boolean(props.disabled)
                })),
                children: helperText
            }),
            /*#__PURE__*/ _jsx(ClassNameConfigurator, {
                disableDefaultClasses: true,
                children: /*#__PURE__*/ _jsx(BaseInput, _object_spread_props(_object_spread({}, props), {
                    ref: ref,
                    error: Boolean(error),
                    id: inputID,
                    slots: {
                        input: mask ? prepareForSlot(TextMaskCustom) : undefined
                    },
                    slotProps: {
                        root: {
                            className: cn(rootClasses({
                                error: Boolean(error),
                                disabled: Boolean(props.disabled),
                                multiline: Boolean(props.multiline)
                            }))
                        },
                        input: {
                            className: cn(inputClasses({
                                size,
                                disabled: props.disabled,
                                startDecorator: Boolean(startDecorator),
                                endDecorator: Boolean(endDecorator)
                            })),
                            mask
                        }
                    },
                    startAdornment: startDecorator && /*#__PURE__*/ _jsx("div", {
                        className: decoratorClasses({
                            multiline: props.multiline,
                            position: 'left'
                        }),
                        children: startDecorator
                    }),
                    endAdornment: endDecorator && /*#__PURE__*/ _jsx("div", {
                        className: decoratorClasses({
                            multiline: props.multiline,
                            position: 'right'
                        }),
                        children: endDecorator
                    })
                }))
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: cn(errorClasses()),
                children: error
            })
        ]
    });
});
export { TextField };
